import { useRef, useState } from 'react';
import { KeyRound, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useEncryption } from '@/context/EncryptionContext';
import { decodeQrFromFile, extractRecoveryKeyFromQr } from '@/utils/qr-scan';
import { toast } from '@/components/ui/use-toast';
import EncryptionScreen from './EncryptionScreen';

const RecoveryKeyUnlock = () => {
  const { unlockWithRecoveryKey, resetEncryption } = useEncryption();
  const [value, setValue] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const unlock = async (key: string) => {
    const trimmed = key.trim();
    if (!trimmed) return;
    setUnlocking(true);
    setError(null);
    try {
      await unlockWithRecoveryKey(trimmed);
    } catch {
      setError("That recovery key doesn't match. Check for typos and try again.");
      setUnlocking(false);
    }
  };

  const onPickQr = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const qr = await decodeQrFromFile(file);
    const key = extractRecoveryKeyFromQr(qr);
    if (!key) {
      toast({
        title: "Couldn't read that QR code",
        description: 'Make sure it is your Jilu recovery code.',
        variant: 'destructive',
      });
      return;
    }
    setValue(key);
    await unlock(key);
  };

  const onReset = async () => {
    setResetting(true);
    try {
      await resetEncryption();
    } catch (err) {
      console.error('Reset failed:', err);
      toast({ title: 'Reset failed', variant: 'destructive' });
      setResetting(false);
    }
  };

  return (
    <EncryptionScreen
      icon={<KeyRound className="h-8 w-8 text-primary mb-2" />}
      title="Enter your recovery key"
    >
      <p className="text-sm text-muted-foreground">
        This device hasn't been unlocked yet. Enter the recovery key you saved
        when you set up Private max mode, or scan its QR code.
      </p>

      <div className="space-y-2">
        <Input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder="XB2K-9F4Q-7T1M-…"
          className="font-mono"
          autoCapitalize="characters"
          autoCorrect="off"
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Button className="flex-1" onClick={() => unlock(value)} disabled={unlocking || !value.trim()}>
          {unlocking ? 'Unlocking…' : 'Unlock'}
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={unlocking}
        >
          <QrCode className="h-4 w-4" />
          Scan QR
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPickQr}
        />
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="w-full text-center text-xs text-muted-foreground hover:text-foreground hover:underline">
            I lost my recovery key
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start over without your old data?</AlertDialogTitle>
            <AlertDialogDescription>
              Without your recovery key, your existing entries can't be opened —
              not by us, not by anyone. You can start fresh with a new key, but
              your old entries will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onReset}
              disabled={resetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {resetting ? 'Starting over…' : 'Start over'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EncryptionScreen>
  );
};

export default RecoveryKeyUnlock;
